'use client';

import { getAbsoluteUrl } from '@repo/routing';
import { useLocale } from 'next-intl';
import { redirect, useRouter } from '../isomorphic';

interface RedirectOptions {
  /** Preserve all search params in the URL during redirect */
  preserveSearchParams?: boolean;
  /** Keep only specified search params in the URL */
  keepSearchParams?: string[];
  /** Force redirect to the new URL, if value is false `router.push` will be used */
  force?: boolean;
}

const computeSearchParams = (path: string, options: RedirectOptions = {}): string => {
  const { preserveSearchParams = true, keepSearchParams } = options;

  const { searchParams } = new URL(getAbsoluteUrl(path));

  if (keepSearchParams?.length) {
    const queryParams = keepSearchParams.map((param) =>
      searchParams.get(param) ? `${param}=${searchParams.get(param)}` : ``,
    );

    return queryParams.join('&');
  }

  if (preserveSearchParams) return searchParams.toString();

  return '';
};

export const useRedirect = () => {
  const locale = useLocale();
  const router = useRouter();

  return (path: string, options: RedirectOptions = {}) => {
    const { force } = options;
    const serializePath = path.replaceAll('//', '/');

    const { pathname } = new URL(getAbsoluteUrl(serializePath));
    const queryParams = computeSearchParams(serializePath, options);

    const redirectPath = queryParams ? `${pathname}?${queryParams}` : pathname;

    if (force) {
      redirect({ href: redirectPath, locale });

      return;
    }

    router.push(redirectPath, { locale });
  };
};
